import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Box, Grid, IconButton, makeStyles, Paper, Chip } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import EventIcon from '@material-ui/icons/Event';

// 樣式定義
const useStyles = makeStyles((theme) => ({
    dialogTitle: {
        padding: theme.spacing(2),
        backgroundColor: "#f7f7f7", 
        borderBottom: '1px solid #ccc',
    },
    customTitleContent: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        fontWeight: "bold",
        width: '100%',
    },
    dialogContent: {
        padding: theme.spacing(3),
    },
    rowLabel: {
        backgroundColor: "#f7f7f7", 
        fontWeight: "bold",
        padding: theme.spacing(1.5),
        borderRight: "1px solid #eee",
        minWidth: 120, 
        textAlign: "left",
        display: 'flex',
        alignItems: 'center',
    },
    rowContent: {
        padding: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
    },
    tableGrid: {
        border: "1px solid #ccc",
    },
    emailChip: {
        margin: theme.spacing(0.5),
        borderColor: "#5597b4",
        color: "#5597b4",
        backgroundColor: "transparent",
        "& svg": {
            color: "#5597b4",
        },
    },
    addButton: {
        backgroundColor: "#5597b4",
        color: "#fff",
        marginLeft: theme.spacing(1),
        "&:hover": {
            backgroundColor: "#3E8CCB",
        },
    },
    saveButton: {
        backgroundColor: "#5597b4",
        color: "#fff",
        "&:hover": {
            backgroundColor: "#3E8CCB",
        },
    },
    cancelButton: {
        color: "#999",
        border: "1px solid #ccc",
        "&:hover": {
            backgroundColor: "#eee",
        },
    },
}));

// Email 格式驗證函式
const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

function EventNotificationDialog({ open, onClose, onSave, windcontract_id, windcontract_name }) {
    const classes = useStyles();
    const windFarmName = windcontract_name || "未知風場名稱"; 
    // 狀態管理
    const [eventName, setEventName] = useState("");
    const [eventContent, setEventContent] = useState("");
    const [note, setNote] = useState("");
    const [notificationDate, setNotificationDate] = useState("2025-10-10"); 
    const [notificationTime, setNotificationTime] = useState("10:10"); 
    const [deadlineDate, setDeadlineDate] = useState("2025-11-11T11:11"); 
    const [newRecipient, setNewRecipient] = useState("");
    const [emails, setEmails] = useState([]);
    const [emailError, setEmailError] = useState("");

    // 處理新增收件者
    const handleAddRecipient = () => {
        if (!newRecipient) return;
        
        const trimmedEmail = newRecipient.trim();

        if (!isValidEmail(trimmedEmail)) {
            setEmailError("Email 格式錯誤");
            return;
        }

        if (emails.includes(trimmedEmail)) {
             setEmailError("此 Email 已存在");
            return;
        }

        setEmails([...emails, trimmedEmail]);
        setNewRecipient("");
        setEmailError("");
    };

    // 處理刪除收件者
    const handleDeleteRecipient = (emailToDelete) => {
        setEmails(emails.filter(email => email !== emailToDelete));
    };

    // 處理儲存
    const handleSave = () => {
        const payload = {
            windcontract_id: windcontract_id,
            event_name: eventName,
            event_content: eventContent,
            note: note,
            notification_date: notificationDate.split('T')[0],
            notification_time: notificationTime,
            deadline_date: deadlineDate,
            emails: emails,
        };

        if (!eventName || !eventContent || !notificationDate || !notificationTime || !deadlineDate) {
            alert("請填寫所有必填欄位 (事件標題、事件內容、通知日期、通知時間、截止日期)");
            return;
        }
        
        console.log("Saving payload:", payload);
        
        onSave({ 
            data: payload,
            status: 1, 
            sheetId: Date.now(),
            type: "create"
        });
        
        onClose();
    };

    const CustomDialogTitle = (props) => {
        const { children, onClose } = props;
        return (
            <DialogTitle disableTypography className={classes.dialogTitle}>
                <div className={classes.customTitleContent}>
                    <Typography variant="h6" style={{ fontWeight: 'bold' }}>{children}</Typography>
                    {onClose ? (
                        <IconButton aria-label="close" onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    ) : null}
                </div>
            </DialogTitle>
        );
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="md"
            fullWidth
            aria-labelledby="event-notification-dialog-title"
        >
            <CustomDialogTitle id="event-notification-dialog-title" onClose={onClose}>
                新增事件提醒
            </CustomDialogTitle>
            
            <DialogContent dividers className={classes.dialogContent}>
                <Paper variant="outlined" className={classes.tableGrid}>
                    {/* 風場名稱 */}
                    <Grid container style={{ borderBottom: '1px solid #eee' }}>
                        <Grid item xs={3} className={classes.rowLabel}>
                            風場名稱
                        </Grid>
                        <Grid item xs={9} className={classes.rowContent}>
                            {windFarmName}
                        </Grid>
                    </Grid>

                    {/* 事件標題 */}
                    <Grid container style={{ borderBottom: '1px solid #eee' }}>
                        <Grid item xs={3} className={classes.rowLabel}>
                            事件標題
                        </Grid>
                        <Grid item xs={9} className={classes.rowContent}>
                            <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={eventName}
                                onChange={(e) => setEventName(e.target.value)} 
                                placeholder="請輸入事件標題"
                            />
                        </Grid>
                    </Grid>

                    {/* 事件內容 */}
                    <Grid container style={{ borderBottom: '1px solid #eee' }}>
                        <Grid item xs={3} className={classes.rowLabel}>
                            事件內容
                        </Grid>
                        <Grid item xs={9} className={classes.rowContent}>
                            <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                multiline
                                rows={3}
                                value={eventContent}
                                onChange={(e) => setEventContent(e.target.value)} 
                                placeholder="請輸入事件內容"
                            />
                        </Grid>
                    </Grid>

                    {/* 備註 */}
                    <Grid container style={{ borderBottom: '1px solid #eee' }}>
                        <Grid item xs={3} className={classes.rowLabel}>
                            備註
                        </Grid>
                        <Grid item xs={9} className={classes.rowContent}>
                            <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                multiline
                                rows={2}
                                value={note}
                                onChange={(e) => setNote(e.target.value)} 
                                placeholder="請輸入備註"
                            />
                        </Grid>
                    </Grid>

                    {/* 通知日期 */}
                    <Grid container style={{ borderBottom: '1px solid #eee' }}>
                        <Grid item xs={3} className={classes.rowLabel}>
                            通知日期
                        </Grid>
                        <Grid item xs={9} className={classes.rowContent}>
                            <TextField
                                variant="outlined"
                                size="small"
                                type="date"
                                value={notificationDate}
                                onChange={(e) => setNotificationDate(e.target.value)}
                            />
                        </Grid>
                    </Grid>

                    {/* 通知時間 */}
                    <Grid container style={{ borderBottom: '1px solid #eee' }}>
                        <Grid item xs={3} className={classes.rowLabel}>
                            通知時間
                        </Grid>
                        <Grid item xs={9} className={classes.rowContent}>
                            <TextField
                                variant="outlined"
                                size="small"
                                type="time"
                                value={notificationTime}
                                onChange={(e) => setNotificationTime(e.target.value)}
                            />
                        </Grid>
                    </Grid>

                    {/* 截止日期 */}
                    <Grid container style={{ borderBottom: '1px solid #eee' }}>
                        <Grid item xs={3} className={classes.rowLabel}>
                            截止日期
                        </Grid>
                        <Grid item xs={9} className={classes.rowContent}>
                            <TextField
                                variant="outlined"
                                size="small"
                                type="datetime-local"
                                value={deadlineDate}
                                onChange={(e) => setDeadlineDate(e.target.value)}
                            />
                        </Grid>
                    </Grid>

                    {/* 新增外部收件者 */}
                    <Grid container style={{ borderBottom: '1px solid #eee' }}>
                        <Grid item xs={3} className={classes.rowLabel}>
                            新增外部收件者
                        </Grid>
                        <Grid item xs={9} className={classes.rowContent}>
                            <Box display="flex" alignItems="flex-start" flexDirection="column">
                                <Box display="flex" alignItems="center" width="100%">
                                    <TextField
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        placeholder="請輸入收件者電子信箱"
                                        value={newRecipient}
                                        onChange={(e) => {
                                            setNewRecipient(e.target.value);
                                            setEmailError(""); 
                                        }}
                                        error={!!emailError}
                                        helperText={emailError}
                                    />
                                    <Button 
                                        variant="contained" 
                                        className={classes.addButton} 
                                        onClick={handleAddRecipient}
                                    >
                                        新增
                                    </Button>
                                </Box>
                                
                                {/* 收件者清單 */}
                                <Box mt={1} width="100%">
                                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                                        收件者清單:
                                    </Typography>
                                    <Box display="flex" flexWrap="wrap">
                                        {emails.map((email) => (
                                            <Chip
                                                key={email}
                                                label={email}
                                                onDelete={() => handleDeleteRecipient(email)}
                                                variant="outlined" 
                                                className={classes.emailChip}
                                                deleteIcon={<CloseIcon />} 
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </DialogContent>

            <DialogActions style={{ padding: '16px 24px' }}>
                <Button 
                    onClick={onClose} 
                    className={classes.cancelButton}
                    variant="outlined"
                >
                    取消
                </Button>
                <Button 
                    onClick={handleSave} 
                    className={classes.saveButton}
                    variant="contained"
                >
                    新增
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default EventNotificationDialog;